import { ContentfulClientApi, Entry, EntryCollection, createClient } from 'contentful';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import React from 'react';
import { Route, Switch } from 'react-router';

import { WithTranslation, withTranslation } from 'react-i18next';
import { ContentfulClientConfig } from 'src/api/contentful/ContentfulClientConfig';
import { ContentType, Page } from 'src/api/contentful/contentTypes.cloverdotcom';
import { fetchEntries } from 'src/api/contentful/fetchEntries';
import { AutoContentfulEntry } from 'src/components/EntryLoader/AutoContentfulEntry';
import { NotFoundPage } from 'src/components/ErrorPage/NotFoundPage';
import { PageAssembly } from 'src/components/PageAssembly/PageAssembly';
import { history } from 'src/store/history';
import { NEW_REDIRECT_ROUTE, getBaseUrlFromLocale } from 'src/utils/AppRoute';
import { componentKey, getField } from 'src/utils/EntryUtil';
import { Environment } from 'src/utils/Environment';
import { LocalStorage } from 'src/utils/LocalStorage';
import { ensureNoTrailingSlash } from 'src/utils/Urls';

const CONTENTFUL_REQUEST_LIMIT = 200;

// Ensures the beginning slash exists, and it has no ending slash
export function normalizeSlug(slug?: string) {
  if (slug === undefined || !slug || slug === '/') return '/';

  const needsPrefix = !slug.startsWith('/');
  const hasSuffix = slug.endsWith('/');

  return `${needsPrefix ? '/' : ''}${hasSuffix ? slug.substr(0, slug.length - 1) : slug}`;
}

export function sortPagesBySlug(items: EntryCollection<Page>['items']) {
  return reverse(sortBy(items, page => getField(page, 'slug', '')));
}

interface IPagesSwitch extends WithTranslation {
  config?: ContentfulClientConfig;
}

type UiState = {
  basicPages?: Array<Entry<Page>>;
};

class PagesSwitch extends React.PureComponent<IPagesSwitch, UiState> {
  static defaultProps: Partial<IPagesSwitch> = {
    config: Environment.getCloverdotcomContentfulConfig(),
  };

  readonly apiClient: ContentfulClientApi;

  readonly state: UiState = {};

  constructor(props: Readonly<IPagesSwitch>, context?: any) {
    super(props, context);

    this.apiClient = createClient(this.props.config!);
  }

  get basicPages() {
    const { basicPages } = this.state;

    return basicPages || [];
  }

  componentDidMount() {
    const pathName = window.location.pathname;
    const queryParams = window.location.search;
    const newUrl = pathName.toLowerCase() + queryParams;
    // tslint:disable-next-line: no-unused-expression
    (pathName.toLowerCase() !== pathName) && history.push(newUrl);
    this.fetch();
  }

  componentDidUpdate(prevProps : IPagesSwitch) {
    // Cannot compare this.props.i18n.language directly: https://github.com/i18next/react-i18next/issues/831
    if (this.props.t !== prevProps.t) {
      this.fetch();
    }
  }

  render() {
    const { children, i18n } = this.props;
    if (!this.state.basicPages) return null;

    // Immediate children of a switch must be Route
    // you cannot wrap it in another component.
    // Switch works by analyzing the immediate (first level) child's props
    return (
      <Switch>
        {this.basicPages.map((entry, index) => {
          const path = normalizeSlug(getField(entry, 'slug'));
          const baseUrl = getBaseUrlFromLocale(i18n.language);
          const isInternationalRedirectOn = LocalStorage.tryGetItem(NEW_REDIRECT_ROUTE) === 'true';
          const newLocalePath = baseUrl && isInternationalRedirectOn ?
            `${ensureNoTrailingSlash(baseUrl)}${path}` :
            path;
          // Required Variable: cannot inline due to TypeScript compilation errors
          const routePath = newLocalePath === '/' ? ['/', '/m/*'] : newLocalePath;

          return (
            <Route
              key={componentKey(entry, index)}
              exact={path === '/'}
              path={routePath}
              render={() => (
                <AutoContentfulEntry
                  entryId={entry.sys.id}
                  include={6}
                  render={entry => <PageAssembly entry={entry} />}
                />
              )}
            />
          );
        })}
        {children}
        <Route render={() => <NotFoundPage />} />
      </Switch>
    );
  }

  async fetchPaginated() : Promise<Array<Entry<Page>>> {
    const { i18n } = this.props;

    let pages : Array<Entry<Page>> = [];

    let numIterations = 0;
    let totalNumPages = -1;
    do {
      const fetchedPages = await fetchEntries<Page>(i18n.language, {
        contentType: ContentType.BASIC_PAGE,
        select: { sys: 'id', fields: ['slug', 'verticalMarket'] },
        order: '-fields.slug',
        limit: CONTENTFUL_REQUEST_LIMIT,
        skip: numIterations * CONTENTFUL_REQUEST_LIMIT,
      }, this.apiClient) as EntryCollection<Page>;

      totalNumPages = fetchedPages.total;
      numIterations++;
      pages = pages.concat(fetchedPages.items);
    } while (pages.length < totalNumPages);

    return pages;
  }

  async fetch() {
    const pages = await this.fetchPaginated();

    this.setState({
      basicPages: sortPagesBySlug(pages),
    });
  }
}

const Translated = withTranslation()(PagesSwitch);
type Translated = typeof Translated;

export {
  Translated as PagesSwitch,
  PagesSwitch as _PagesSwitch,
};
